/**
 * WantokChat — ICER Health AI Health Navigation Assistant widget.
 *
 * Phase 1A+ (Tier A polish):
 *  - Full i18n: every visible string translates per language toggle
 *  - Time-of-day + name-personalised opening greeting
 *  - Message timestamps
 *  - "New conversation" button in header
 *  - Patient name in header subtitle (patient channel)
 *
 * Tok Pisin strings reviewed and approved by Dr Issac W Rami, MMed
 * (Emergency Medicine), National Capital District, 14 May 2026.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import "./WantokChat.css";

// ---------------------------------------------------------------------------
// UI string table (full i18n)
// ---------------------------------------------------------------------------

const UI_STRINGS = {
  en: {
    tokLabel: "Lang:",
    closeChat: "Close chat",
    openChat: "Open chat",
    newChat: "New conversation",
    signUp: "Sign up",
    signUpCta: "Create my ICER Health account →",
    send: "Send",
    placeholder: "Type your message…",
    disclaimerPatient:
      "I am an AI Health Navigation Assistant — not a doctor. A real ICER Health clinician will review every routing decision. In emergencies I will dispatch an ambulance.",
    disclaimerPublic:
      "I am an AI Navigator on the public site. For any health concern, please sign up so a real ICER Health clinician can help you safely.",
    emergencyBanner: "This sounds like an emergency.",
    emergencyAction: "Request ambulance now",
    consentPrompt:
      "Before chatting, please review and accept the consent for the Wantok Clinical Navigator.",
    consentBtn: "Review and accept",
    networkError: "⚠ Network error. Please check your connection and try again.",
    serverError: "⚠ Sorry, there was a problem reaching the assistant. Please try again.",
    sessionEnded: "✓ This conversation is complete. Start a new one whenever you'd like.",
    footerPatient: "Powered by ICER Health · Clinical AI Navigation (human reviewed)",
    footerPublic: "Powered by ICER Health · AI Health Navigation only",
    typingLabel: "Assistant is typing",
    chatLabel: "Wantok Assistant chat",
    headerSubPublic: "AI Health Navigation · ICER Health",
    headerSubPatientFallback: "AI Health Navigation · Signed in",
    headerSubPatientNamed: (name) => `AI Health Navigation · ${name}`,
  },
  tpi: {
    tokLabel: "Tok:",
    closeChat: "Pasim toktok",
    openChat: "Opim toktok",
    newChat: "Nupela toktok",
    signUp: "Sain ap",
    signUpCta: "Wokim akaun bilong mi long ICER Health →",
    send: "Salim",
    placeholder: "Raitim toktok bilong yu long hia",
    disclaimerPatient:
      "Mi wanpela komputa long helpim yu — mi no dokta. Wanpela dokta tru bilong ICER Health stap wantaim yu klostu taim long soim yu long hap we yu ken kisim helpim. Long taim bilong emergency, mi bai singautim ambulans.",
    disclaimerPublic:
      "Mi komputa bai helpim yu long toktok wantain trutru ICER Health dokta. Em bai helpim yu.",
    emergencyBanner: "Luk olsem yu mas go kwiktaim long hausik.",
    emergencyAction: "Singautim ambulans nau",
    consentPrompt:
      "Bipo yu toktok, plis ritim na givim tok orait long yusim Wantok Clinical Navigator.",
    consentBtn: "Mi tok Orait",
    networkError: "⚠ Network i nogat. Sekim koneksen na traim gen.",
    serverError: "⚠ Sori, gat liklik hevi long Network. Traim gen liklik taim bihain..",
    sessionEnded: "✓ Toktok bilong yu i pinis. Stat nupela toktok long taim yu laik.",
    footerPatient: "ICER Health i wokim dispela nupela rot blong sikman i kisim helpim (tru tru man wokim).",
    footerPublic: "ICER Health i stretim rot tasol blong painim helpim.",
    typingLabel: "Wantok rait istap",
    chatLabel: "Tokim Wantok long helpim",
    headerSubPublic: "AI Health Navigation · ICER Health",
    headerSubPatientFallback: "AI Health Navigation · Yu sain in",
    headerSubPatientNamed: (name) => `AI Health Navigation · ${name}`,
  },
  ho: {
    tokLabel: "Hereva:",
    closeChat: "Hereva koua",
    openChat: "Hereva kehoa",
    newChat: "Hereva matamata",
    signUp: "Sign-up karaia",
    signUpCta: "Egu ICER Health account hamatamaia →",
    send: "Siaia",
    placeholder: "Emu hereva torea…",
    disclaimerPatient:
      "Lau be AI Durua tauna — lau be doctor lasi. ICER Health doctor korikorina ese routing decision iboudiai do ia itadia. Hekwarahi lalonai, lau ese ambulance do lau siaia.",
    disclaimerPublic:
      "Lau be AI Navigator public site dekenai. Hisihisi ta dainai, mani sign-up karaia bona ICER Health doctor korikorina ese do ia durua oi.",
    emergencyBanner: "Inai be hekwarahi bamona.",
    emergencyAction: "Ambulance request henia hari",
    consentPrompt:
      "Hereva oi karaia lalonai, Wantok Clinical Navigator consent itaia bona abia dae.",
    consentBtn: "Itaia bona abia dae",
    networkError: "⚠ Network ia lasi. Connection itaia bona lou tohoa.",
    serverError: "⚠ Hisi, assistant kainai hereva be problem ta vara. Lou tohoa.",
    sessionEnded: "✓ Inai hereva ia ore. Hereva matamata oi ura neganai do oi hamatamaia.",
    footerPatient: "ICER Health ese karaia · Clinical AI Navigation (tau ese ia itaia)",
    footerPublic: "ICER Health ese karaia · AI Health Navigation sibona",
    typingLabel: "Wantok ia hereva noho",
    chatLabel: "Wantok Assistant hereva",
    headerSubPublic: "AI Health Navigation · ICER Health",
    headerSubPatientFallback: "AI Health Navigation · Sign-in vada",
    headerSubPatientNamed: (name) => `AI Health Navigation · ${name}`,
  },
};

function t(language, key, ...args) {
  const table = UI_STRINGS[language] || UI_STRINGS.en;
  const val = table[key];
  if (typeof val === "function") return val(...args);
  return val ?? UI_STRINGS.en[key];
}

// ---------------------------------------------------------------------------
// Greeting composition (time-of-day + name)
// ---------------------------------------------------------------------------

function timeOfDayGreeting(language) {
  const hour = new Date().getHours();
  const table = {
    en: hour < 5 ? "Hello" : hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening",
    tpi: hour < 5 ? "Halo" : hour < 12 ? "Monin" : hour < 18 ? "Apinun" : "Gutpela Nait",
    ho: hour < 5 ? "Maino" : hour < 12 ? "Daba namo" : hour < 17 ? "Adorahi namo" : "Hanuaboi namo",
  };
  return table[language] || table.en;
}

function composeGreeting({ language, channel, patientName }) {
  const tod = timeOfDayGreeting(language);
  const trimmedName = (patientName || "").toString().trim().split(" ")[0];
  const namePart = trimmedName ? `, ${trimmedName}` : "";

  if (channel === "patient") {
    const templates = {
      en: `${tod}${namePart}. I'm Wantok Clinical Navigator. I can help you book a consult, lab test, imaging, ambulance, or prescription — or just talk through how you're feeling. What's on your mind today?`,
      tpi: `${tod}${namePart}. Wantok, mi inap helpim yu long bukim dokta, lab, imaging, ambulans, o hap blong kisim marasin — o yu igat sampela askim long sik blong yu?`,
      ho: `${tod}${namePart}. Lau be Wantok Clinical Navigator. Doctor, lab, imaging, ambulance, o marasin — lau be oi durua diba. Dahaka oi ura herevalaia?`,
    };
    return templates[language] || templates.en;
  }

  const templates = {
    en: `${tod}. I'm ICER Health Navigator. I can answer questions about our services, subscriptions, and how to sign up. What would you like to know?`,
    tpi: `${tod}. Mi ICER Health Wantok. Mi inap bekim ol askim long ol sevis bilong mipela na olsem wanem bai yu sain ap. Wanem moa askim yu igat or yu laik save?`,
    ho: `${tod}. Lau be ICER Health Navigator. Mai sevis, subscription, bona sign-up dainai, lau ese henanadai do lau haere. Dahaka oi ura diba?`,
  };
  return templates[language] || templates.en;
}

const QUICK_REPLIES = {
  public: {
    en: ["What is ICER Health?", "How do I sign up?", "What does it cost?", "Which provinces?"],
    tpi: ["ICER Health em wanem?", "Olsem wanem bai mi sain ap?", "Em i kostim wanem?", "Wanem provins?"],
    ho: ["ICER Health be dahaka?", "Edena bamona sign-up?", "Davana be dahaka?", "Edena province?"],
  },
  patient: {
    en: ["I feel sick", "Book a doctor", "Find a lab", "Need ambulance", "Refill prescription"],
    tpi: ["Mi sik", "Bukim dokta", "Painim lab", "Mi nidim ambulans", "Nidim ol suplai marasin bilong mi gen"],
    ho: ["Lau be hisihisi", "Doctor tahua", "Lab tahua", "Ambulance ura", "Marasin"],
  },
};

const newSessionId = () =>
  "sess_" + Math.random().toString(36).slice(2) + Date.now().toString(36);

function fmtTime(d) {
  if (!d) return "";
  try {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function WantokChat({
  apiBase = "/api/chat/",
  defaultLanguage = "en",
  initialOpen = false,
  channel = "public",
  jwtToken = null,
  patientName = null,
  consentVersion = null,
  consentAccepted = false,
  onConsentRequired = null,
  signupUrl = "/register",
  bookUrl = "/patient/book",
}) {
  const isPatientChannel = channel === "patient";

  const [open, setOpen] = useState(initialOpen);
  const [language, setLanguage] = useState(defaultLanguage);
  const [sessionId, setSessionId] = useState(() => {
    if (typeof window === "undefined") return newSessionId();
    const key = isPatientChannel ? "icerPatientSessionId" : "icerSessionId";
    return sessionStorage.getItem(key) || newSessionId();
  });
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [showEmergency, setShowEmergency] = useState(false);
  const [showSignupCta, setShowSignupCta] = useState(false);
  // Set when the triage grade is amber/green and a booking intent exists,
  // so the patient can self-book. Red is NOT offered self-booking — it
  // shows the emergency banner and routes to Triage-Ops.
  const [triageBooking, setTriageBooking] = useState(null);
  const [quickReplies, setQuickReplies] = useState(null);
  const [needsConsent, setNeedsConsent] = useState(false);
  const messagesEndRef = useRef(null);
  const textRef = useRef(null);

  const sessionKey = isPatientChannel ? "icerPatientSessionId" : "icerSessionId";

  const greetingText = useMemo(
    () => composeGreeting({ language, channel, patientName }),
    [language, channel, patientName]
  );
  const quickRepliesMap = QUICK_REPLIES[isPatientChannel ? "patient" : "public"];

  const firstName = useMemo(
    () => (patientName || "").toString().trim().split(" ")[0] || "",
    [patientName]
  );

  const headerSubtitle = isPatientChannel
    ? (firstName ? t(language, "headerSubPatientNamed", firstName) : t(language, "headerSubPatientFallback"))
    : t(language, "headerSubPublic");

  useEffect(() => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem(sessionKey, sessionId);
    }
  }, [sessionId, sessionKey]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, sending]);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "bot", content: greetingText, ts: new Date() }]);
    }
    if (open) setTimeout(() => textRef.current?.focus(), 100);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isPatientChannel && open && !consentAccepted && messages.length <= 1) {
      setNeedsConsent(true);
    } else {
      setNeedsConsent(false);
    }
  }, [open, consentAccepted, messages.length, isPatientChannel]);

  const rotateSession = () => {
    const id = newSessionId();
    setSessionId(id);
    setMessages([]);
    setShowEmergency(false);
  };

  const startNewChat = () => {
    const id = newSessionId();
    setSessionId(id);
    setShowEmergency(false);
    setMessages([{ role: "bot", content: greetingText, ts: new Date() }]);
    setTimeout(() => textRef.current?.focus(), 50);
  };

  const handleLangChange = (lang) => {
    if (lang === language) return;
    setLanguage(lang);
    rotateSession();
    const fresh = composeGreeting({ language: lang, channel, patientName });
    setMessages([{ role: "bot", content: fresh, ts: new Date() }]);
  };

  const sendMessage = async (msg) => {
    const trimmed = (msg || "").trim();
    if (!trimmed || sending) return;
    if (isPatientChannel && !consentAccepted) {
      setNeedsConsent(true);
      if (onConsentRequired) onConsentRequired();
      return;
    }

    setMessages((prev) => [...prev, { role: "user", content: trimmed, ts: new Date() }]);
    setText("");
    setSending(true);
    // Clear any previous emergency banner / signup CTA — only THIS message can re-raise them
    setShowEmergency(false);
    setShowSignupCta(false);
    setTriageBooking(null);
    setQuickReplies(null);

    try {
      const headers = { "Content-Type": "application/json" };
      if (isPatientChannel && jwtToken) {
        headers["Authorization"] = `Bearer ${jwtToken}`;
      }
      const body = { message: trimmed, sessionId, language };
      if (isPatientChannel) body.consentVersion = consentVersion || "v1";

      const res = await fetch(apiBase, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        setMessages((prev) => [...prev, { role: "bot", content: t(language, "serverError"), ts: new Date() }]);
        return;
      }

      const data = await res.json();
      setMessages((prev) => [...prev, { role: "bot", content: data.reply || "…", ts: new Date() }]);
      setQuickReplies(data.quick_replies || null);
      if (data.flags?.isEmergency) setShowEmergency(true);
      if (data.flags?.hasSignupIntent) setShowSignupCta(true);
      if (
        isPatientChannel &&
        data.triage &&
        data.triage.booking_intent_id &&
        (data.triage.severity === "amber" || data.triage.severity === "green")
      ) {
        setTriageBooking({ id: data.triage.booking_intent_id, severity: data.triage.severity });
      }

      if (data.flags?.sessionEnded) {
        setTimeout(() => {
          const id = newSessionId();
          setSessionId(id);
          setMessages((prev) => [...prev, { role: "system", content: t(language, "sessionEnded"), ts: new Date() }]);
        }, 800);
      }
    } catch (e) {
      setMessages((prev) => [...prev, { role: "bot", content: t(language, "networkError"), ts: new Date() }]);
    } finally {
      setSending(false);
      setTimeout(() => textRef.current?.focus(), 50);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(text);
    }
  };

  const showQuickReplies = messages.length === 1 && !sending && !needsConsent;

  return (
    <div className="icer-chat">
      <button
        className="icer-chat-launcher"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? t(language, "closeChat") : t(language, "openChat")}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
        </svg>
        <span className="icer-chat-badge">{isPatientChannel ? "Rx" : "AI"}</span>
      </button>

      {open && (
        <div className="icer-chat-window" role="dialog" aria-label={t(language, "chatLabel")}>
          <div className="icer-chat-header">
            <div className="icer-chat-avatar">W</div>
            <div className="icer-chat-title">
              <h3>{isPatientChannel ? "Wantok Clinical Navigator" : "ICER Health Navigator"}</h3>
              <p>{headerSubtitle}</p>
            </div>
            {!isPatientChannel && (
              <a
                className="icer-chat-signup-pill"
                href={signupUrl}
                title={t(language, "signUp")}
                aria-label={t(language, "signUp")}
              >
                {t(language, "signUp")}
              </a>
            )}
            <button
              className="icer-chat-new"
              onClick={startNewChat}
              type="button"
              aria-label={t(language, "newChat")}
              title={t(language, "newChat")}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" fill="none" />
              </svg>
            </button>
            <button
              className="icer-chat-close"
              onClick={() => setOpen(false)}
              aria-label={t(language, "closeChat")}
            >
              ×
            </button>
          </div>

          <div className="icer-chat-lang">
            <span>{t(language, "tokLabel")}</span>
            <button className={language === "en" ? "active" : ""} onClick={() => handleLangChange("en")} type="button">English</button>
            <button className={language === "tpi" ? "active" : ""} onClick={() => handleLangChange("tpi")} type="button">Tok Pisin</button>
            {isPatientChannel && (
              <button className={language === "ho" ? "active" : ""} onClick={() => handleLangChange("ho")} type="button">Hiri Motu</button>
            )}
          </div>

          <div className="icer-chat-disclaimer">
            {isPatientChannel ? t(language, "disclaimerPatient") : t(language, "disclaimerPublic")}
          </div>

          {showEmergency && (
            <div className="icer-chat-emergency">
              <span className="icer-chat-emergency-text">
                🚨 {t(language, "emergencyBanner")}{" "}
                {/* Auth-aware routing (2026-07-24 fix): authenticated patients
                    go straight to the ambulance marketplace on their dashboard;
                    public visitors are routed to sign-up first (same URL the
                    header Sign up pill uses). Previously this was
                    href="#book-ambulance" which was a nowhere-fragment,
                    stranding users on /login#book-ambulance. */}
                <a href={channel === "patient" ? "/patient/ambulance" : signupUrl}>
                  {t(language, "emergencyAction")}
                </a>
              </span>
              <button
                className="icer-chat-emergency-close"
                onClick={() => setShowEmergency(false)}
                type="button"
                aria-label={t(language, "closeChat")}
                title="Dismiss"
              >
                ×
              </button>
            </div>
          )}

          {needsConsent && (
            <div className="icer-chat-consent">
              <p>{t(language, "consentPrompt")}</p>
              <button
                className="icer-chat-consent-btn"
                onClick={() => onConsentRequired && onConsentRequired()}
                type="button"
              >
                {t(language, "consentBtn")}
              </button>
            </div>
          )}

          <div className="icer-chat-messages" aria-live="polite">
            {messages.map((m, i) => (
              <div key={i} className={`icer-chat-msg-wrap ${m.role}`}>
                <div className={`icer-chat-msg ${m.role}`}>{m.content}</div>
                <div className="icer-chat-msg-time">{fmtTime(m.ts)}</div>
              </div>
            ))}
            {sending && (
              <div className="icer-chat-typing" aria-label={t(language, "typingLabel")}>
                <span></span><span></span><span></span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {showQuickReplies && (
            <div className="icer-chat-quick">
              {(quickRepliesMap[language] || quickRepliesMap.en).map((q, i) => (
                <button key={i} onClick={() => sendMessage(q)} type="button">{q}</button>
              ))}
            </div>
          )}

          {quickReplies && !sending && !needsConsent && !showEmergency && (
            <DynamicQuickReplies
              spec={quickReplies}
              onPick={(val) => sendMessage(val)}
            />
          )}

          {isPatientChannel && triageBooking && !showEmergency && (
            <a
              className="icer-chat-signup-cta"
              href={`${bookUrl}?triage=${triageBooking.id}`}
              onClick={() => setTriageBooking(null)}
            >
              {triageBooking.severity === "amber"
                ? "Book a consultation soon"
                : "Book your consultation"}
            </a>
          )}

          {!isPatientChannel && showSignupCta && (
            <a
              className="icer-chat-signup-cta"
              href={signupUrl}
              onClick={() => setShowSignupCta(false)}
            >
              {t(language, "signUpCta")}
            </a>
          )}

          <div className="icer-chat-input">
            <textarea
              ref={textRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t(language, "placeholder")}
              rows={1}
              disabled={sending || needsConsent}
            />
            <button
              onClick={() => sendMessage(text)}
              disabled={sending || !text.trim() || needsConsent}
              aria-label={t(language, "send")}
              title={t(language, "send")}
              type="button"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M2 21l21-9L2 3v7l15 2-15 2z" />
              </svg>
            </button>
          </div>

          <div className="icer-chat-footer">
            {isPatientChannel ? t(language, "footerPatient") : t(language, "footerPublic")}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Guided quick-reply widget (Phase A of structured intake). Renders the
 * assistant's [[QUICKREPLIES]] spec as tappable buttons — severity scale,
 * single/multi chips, duration, or yes/no. A tap posts the chosen value as the
 * next message. The free-text box always stays available (rendered separately),
 * and every widget offers a "Skip" / "I'm not sure" escape.
 */
function DynamicQuickReplies({ spec, onPick }) {
  const [selected, setSelected] = useState([]);
  if (!spec || !spec.type) return null;

  const promptEl = spec.prompt ? (
    <div style={{ width: "100%", fontSize: 12, color: "#64748b", marginBottom: 4 }}>
      {spec.prompt}
    </div>
  ) : null;

  if (spec.type === "severity") {
    const min = Number.isFinite(spec.min) ? spec.min : 0;
    const max = Number.isFinite(spec.max) ? spec.max : 10;
    const nums = [];
    for (let n = min; n <= max; n++) nums.push(n);
    return (
      <div className="icer-chat-quick">
        {promptEl}
        {nums.map((n) => (
          <button key={n} type="button" onClick={() => onPick(`${n}/${max}`)}>{n}</button>
        ))}
        <button type="button" onClick={() => onPick("I'm not sure")}>I'm not sure</button>
      </div>
    );
  }

  if (spec.type === "yes_no") {
    return (
      <div className="icer-chat-quick">
        {promptEl}
        <button type="button" onClick={() => onPick("Yes")}>Yes</button>
        <button type="button" onClick={() => onPick("No")}>No</button>
        <button type="button" onClick={() => onPick("I'm not sure")}>I'm not sure</button>
      </div>
    );
  }

  // chips / duration
  const options = Array.isArray(spec.options) ? spec.options : [];
  if (spec.multi) {
    const toggle = (o) =>
      setSelected((cur) => (cur.includes(o) ? cur.filter((x) => x !== o) : [...cur, o]));
    return (
      <div className="icer-chat-quick">
        {promptEl}
        {options.map((o) => {
          const on = selected.includes(o);
          return (
            <button
              key={o}
              type="button"
              onClick={() => toggle(o)}
              style={on ? { background: "#0f766e", color: "#fff", borderColor: "#0f766e" } : undefined}
            >
              {on ? `✓ ${o}` : o}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => onPick(selected.length ? selected.join(", ") : "None of these")}
        >
          {selected.length ? "Send answer" : "None of these"}
        </button>
      </div>
    );
  }

  return (
    <div className="icer-chat-quick">
      {promptEl}
      {options.map((o) => (
        <button key={o} type="button" onClick={() => onPick(o)}>{o}</button>
      ))}
      <button type="button" onClick={() => onPick("Skip")}>Skip</button>
    </div>
  );
}
